// /foto/galleri — samlat bildgalleri för affärsområdet Foto.
// Egen URL så att galleriet kan indexeras, länkas i sitemap och plockas upp
// av Google Bilder med ImageGallery-schema och beskrivande alt-texter.

import { createFileRoute, Link } from "@tanstack/react-router";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { CATEGORY_LABELS, galleryPhotos, photosBy } from "@/content/photo-gallery";
import { fotoCities, PHOTOGRAPHER } from "@/content/foto";
import { aeoGeo, breadcrumbs, imageGallery, speakable } from "@/lib/seo/schema";

const PATH = "/foto/galleri";
const TITLE = "Bildgalleri — fotografering för företag | Fenny Foto";
const DESCRIPTION =
  "Hela Fennys fotoportfolio på ett ställe: företagsporträtt, kampanjbilder, studiofoto och drönarfoto. Filtrera per kategori och se manéret innan ni bokar.";
const ANSWER = `Fennys bildgalleri samlar ${galleryPhotos.length} publicerade bilder ur levererade fotouppdrag: företagsporträtt och headshots, kampanj- och varumärkesbilder, studiofoto samt drönarfoto. Varje bild har en beskrivande alt-text med motiv, ljussättning och format, och galleriet kan filtreras per kategori. Fotograf och kontaktperson är ${PHOTOGRAPHER.name}, hey@fenny.se.`;

const CATEGORIES = ["portratt", "kampanj", "studio", "dronare"] as const;

export const Route = createFileRoute("/foto/galleri")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Bildgalleri — Fenny Foto" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Var kan man se Fennys bilder?",
            answer: ANSWER,
            speakableSelectors: ["[data-quick-answer]"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: "Fenny Foto — bildgalleri",
            path: PATH,
            description: DESCRIPTION,
            images: galleryPhotos,
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Bildgalleri", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: FotoGalleriPage,
});

function FotoGalleriPage() {
  return (
    <article className="pt-28">
      <Section>
        <EyebrowHeading
          as="h1"
          eyebrow="Bildgalleri"
          title="Hela portfolion — porträtt, kampanj, studio och drönare"
          lede={
            <span className="lede">
              {galleryPhotos.length} bilder ur levererade uppdrag. Filtrera per kategori,
              öppna i helskärm och se ljussättning och manér innan ni bokar.
            </span>
          }
        />
        <div className="mt-8">
          <QuickAnswer text={ANSWER} />
        </div>
      </Section>

      <PhotoMasonry
        photos={galleryPhotos}
        filterable
        initial={24}
        eyebrow="Alla bilder"
        heading="Bildgalleri"
      />

      <Section className="bg-surface/30">
        <EyebrowHeading
          eyebrow="Kategorier"
          title="Bläddra per bildtyp"
          lede="Varje kategori har en egen tjänstesida med upplägg, tidsåtgång och prisbild."
        />
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((c) => (
            <div key={c} className="rounded-2xl border border-white/10 bg-surface/50 p-6">
              <h2 className="text-lg font-semibold">{CATEGORY_LABELS[c]}</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {photosBy(c).length} bilder i galleriet.
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <EyebrowHeading
          eyebrow="Fotograf nära er"
          title="Vi fotograferar i hela Sverige"
          lede="Bilderna ovan är fotograferade på uppdrag åt svenska bolag. Välj er ort för lokalt upplägg och prisbild."
        />
        <div className="mt-8 flex flex-wrap gap-2">
          {fotoCities.map((c) => (
            <a
              key={c.slug}
              href={c.path}
              className="rounded-full border border-white/10 bg-surface/40 px-4 py-2 text-sm transition hover:border-mint/50 hover:text-mint"
            >
              Fotograf {c.city}
            </a>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Tillbaka till{" "}
          <Link to="/foto" className="underline decoration-mint/40 underline-offset-4 hover:text-mint">
            foto-hubben
          </Link>
          .
        </p>
      </Section>

      <FotoQuoteForm location="foto_galleri" />
    </article>
  );
}
